import { useCallback, useEffect, useState } from "react";
import { updatePoint as apiUpdatePoint } from "../api";
import {
  getApiErrorMessage,
  getConflictCurrent,
  isConflictError,
} from "../api/client";
import { applyPointServerFields } from "../utils/pointState";

const POINT_RESULT_STATUSES = new Set(["Pozitif", "Negatif", "Tekrar", "pending"]);

function normalizePointResultStatus(value) {
  const text = String(value || "").trim();
  return POINT_RESULT_STATUSES.has(text) ? text : "pending";
}

export function useMeasurements({
  points,
  setPoints,
  isOnline,
  addToSyncQueue,
  clickedInfo,
  measurementTemplate,
}) {
  const [sicaklik, setSicaklik] = useState("");
  const [atm, setAtm] = useState("");
  const [spc, setSpc] = useState("");
  const [tds, setTds] = useState("");
  const [sal, setSal] = useState("");
  const [ph, setPh] = useState("");
  const [notMetni, setNotMetni] = useState("");
  const [customMeasurements, setCustomMeasurements] = useState({});
  const [pointPhotos, setPointPhotos] = useState([]);
  const [entryStatus, setEntryStatus] = useState("pending");
  const [showVeriPopup, setShowVeriPopup] = useState(false);

  const resetFields = useCallback(() => {
    setSicaklik("");
    setAtm("");
    setSpc("");
    setTds("");
    setSal("");
    setPh("");
    setNotMetni("");
    setCustomMeasurements({});
    setPointPhotos([]);
    setEntryStatus("pending");
    setShowVeriPopup(false);
  }, []);

  useEffect(() => {
    if (!showVeriPopup) return;

    const pointId = clickedInfo?.id;
    const paftaId = clickedInfo?.paftaId;
    const point =
      (points || []).find(
        (item) => item.id === pointId && (!paftaId || item.paftaId === paftaId)
      ) || clickedInfo;

    setCustomMeasurements(
      point?.customMeasurements && typeof point.customMeasurements === "object"
        ? { ...point.customMeasurements }
        : {}
    );

    setNotMetni(typeof point?.notMetni === "string" ? point.notMetni : "");
    const fromArray = Array.isArray(point?.photoDataUrls)
      ? point.photoDataUrls
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean)
      : [];
    const fromLegacy =
      typeof point?.photoDataUrl === "string" && point.photoDataUrl.trim()
        ? [point.photoDataUrl.trim()]
        : [];
    setPointPhotos((fromArray.length ? fromArray : fromLegacy).slice(0, 4));
    setEntryStatus(normalizePointResultStatus(point?.status || clickedInfo?.status));
    setSicaklik(point?.sicaklik ?? "");
    setAtm(point?.atm ?? "");
    setSpc(point?.spc ?? "");
    setTds(point?.tds ?? "");
    setSal(point?.sal ?? "");
    setPh(point?.ph ?? "");
  }, [showVeriPopup, clickedInfo?.id, clickedInfo?.paftaId]);

  const handleVeriPopupSave = async () => {
    if (!clickedInfo?.id || !clickedInfo?.paftaId) {
      alert("Veri kaydedilemedi, nokta bilgisi eksik.");
      return false;
    }

    const textValues = [sicaklik, atm, spc, tds, sal, ph].map((value) =>
      (value ?? "").toString().trim()
    );
    const currentPoint = points.find((item) => item.id === clickedInfo.id) || clickedInfo;
    const baseUpdatedAt = currentPoint?.updatedAt ?? null;
    const nextStatus = normalizePointResultStatus(entryStatus);
    const currentStatus = normalizePointResultStatus(currentPoint?.status);
    const statusChanged = nextStatus !== currentStatus;
    const isPositiveEntry = nextStatus === "Pozitif";

    const noteHasValue = (notMetni ?? "").toString().trim() !== "";
    const normalizedPointPhotos = (Array.isArray(pointPhotos) ? pointPhotos : [])
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean)
      .slice(0, 4);
    const currentPointPhotosFromArray = Array.isArray(currentPoint?.photoDataUrls)
      ? currentPoint.photoDataUrls
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean)
      : [];
    const currentPointPhotos =
      currentPointPhotosFromArray.length > 0
        ? currentPointPhotosFromArray.slice(0, 4)
        : typeof currentPoint?.photoDataUrl === "string" && currentPoint.photoDataUrl.trim()
          ? [currentPoint.photoDataUrl.trim()]
          : [];
    const photoHasValue = normalizedPointPhotos.length > 0;
    const photoChanged =
      JSON.stringify(currentPointPhotos) !== JSON.stringify(normalizedPointPhotos);
    const visibleTemplateRows = (measurementTemplate || []).filter(
      (row) => row?.visible !== false
    );
    const customRowIds = visibleTemplateRows
      .filter((row) => !row?.bind && row?.id)
      .map((row) => row.id);

    const customHasValue = customRowIds.some((rowId) => {
      const value = customMeasurements?.[rowId];
      return value != null && String(value).trim() !== "";
    });

    if (!isPositiveEntry) {
      if (!statusChanged && !photoChanged) {
        alert("Durum değişikliği veya fotoğraf ekleyin.");
        return false;
      }

      const now = Date.now();
      const updates = {};

      if (statusChanged) {
        updates.status = nextStatus;
        updates.statusUpdatedAt = now;
      }

      if (photoChanged) {
        updates.photoDataUrl = normalizedPointPhotos[0] || "";
        updates.photoDataUrls = normalizedPointPhotos;
      }

      setPoints((previous) =>
        previous.map((point) =>
          point.id === clickedInfo.id ? { ...point, ...updates } : point
        )
      );

      try {
        if (!isOnline) {
          await addToSyncQueue({
            type: "updatePoint",
            pointId: clickedInfo.id,
            baseUpdatedAt,
            updates,
          });
        } else {
          const updatedPoint = await apiUpdatePoint(clickedInfo.id, {
            ...updates,
            baseUpdatedAt,
          });
          setPoints((previous) =>
            applyPointServerFields(
              previous,
              clickedInfo.id,
              updatedPoint,
              Object.keys(updates)
            )
          );
        }

        resetFields();
        setShowVeriPopup(false);
        return true;
      } catch (error) {
        if (isConflictError(error)) {
          const current = getConflictCurrent(error);
          if (current?.id) {
            setPoints((previous) =>
              previous.map((point) =>
                point.id === current.id ? { ...point, ...current } : point
              )
            );
          }
          alert(
            getApiErrorMessage(
              error,
              "Nokta başka bir oturumda güncellendi. Sunucudaki güncel veri geri yüklendi."
            )
          );
          return false;
        }

        await addToSyncQueue({
          type: "updatePoint",
          pointId: clickedInfo.id,
          baseUpdatedAt,
          updates,
        });
        alert("Sunucuya ulaşılamadı, veri kuyruğa alındı.");
        resetFields();
        setShowVeriPopup(false);
        return true;
      }
    }

    if (
      textValues.every((value) => value === "") &&
      !noteHasValue &&
      !customHasValue &&
      !photoHasValue &&
      !photoChanged &&
      !statusChanged
    ) {
      alert("Lütfen en az bir alanı doldurun.");
      return false;
    }

    const pointLat = clickedInfo.lat ?? null;
    const pointLng = clickedInfo.lng ?? null;

    if (pointLat == null || pointLng == null) {
      alert("Noktanın koordinat bilgisi bulunamadı.");
      return false;
    }

    const now = Date.now();
    const updates = {
      sicaklik: textValues[0],
      atm: textValues[1],
      spc: textValues[2],
      tds: textValues[3],
      sal: textValues[4],
      ph: textValues[5],
      lat: pointLat,
      lng: pointLng,
      veriGirisiTimestamp: now,
      notMetni,
      customMeasurements,
      photoDataUrl: normalizedPointPhotos[0] || "",
      photoDataUrls: normalizedPointPhotos,
    };

    if (statusChanged) {
      updates.status = nextStatus;
      updates.statusUpdatedAt = now;
    }

    setPoints((previous) =>
      previous.map((point) =>
        point.id === clickedInfo.id ? { ...point, ...updates } : point
      )
    );

    try {
      if (!isOnline) {
        await addToSyncQueue({
          type: "updatePoint",
          pointId: clickedInfo.id,
          baseUpdatedAt,
          updates,
        });
      } else {
        try {
          const updatedPoint = await apiUpdatePoint(clickedInfo.id, {
            ...updates,
            baseUpdatedAt,
          });
          setPoints((previous) =>
            applyPointServerFields(
              previous,
              clickedInfo.id,
              updatedPoint,
              Object.keys(updates)
            )
          );
        } catch (error) {
          if (isConflictError(error)) {
            const current = getConflictCurrent(error);
            if (current?.id) {
              setPoints((previous) =>
                previous.map((point) =>
                  point.id === current.id ? { ...point, ...current } : point
                )
              );
            }
            alert(
              getApiErrorMessage(
                error,
                "Nokta başka bir oturumda güncellendi. Sunucudaki güncel veri geri yüklendi."
              )
            );
            return false;
          }

          await addToSyncQueue({
            type: "updatePoint",
            pointId: clickedInfo.id,
            baseUpdatedAt,
            updates,
          });
          alert("Sunucuya ulaşılamadı, veri kuyruğa alındı.");
        }
      }

      resetFields();
      setShowVeriPopup(false);
      return true;
    } catch (error) {
      alert("Kaydetme sırasında bir hata oluştu.");
      return false;
    }
  };

  return {
    sicaklik,
    setSicaklik,
    atm,
    setAtm,
    spc,
    setSpc,
    tds,
    setTds,
    sal,
    setSal,
    ph,
    setPh,
    notMetni,
    setNotMetni,
    customMeasurements,
    setCustomMeasurements,
    pointPhotos,
    setPointPhotos,
    entryStatus,
    setEntryStatus,
    showVeriPopup,
    setShowVeriPopup,
    handleVeriPopupSave,
    resetFields,
  };
}
